import {
  Box,
  Flex,
  Grid,
  Input,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  useBreakpointValue,
  IconButton,
  Button,
  Textarea,
  FormControl,
  FormLabel,
  Spinner,
  GridItem,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from "@chakra-ui/react";
import { CloseIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "AppProvider/AppContext";
import FileViewer, { Sample } from "components/common/FileViewerPdfAndImage/FileViewer";
import { appRequest } from "routes";
import { PDFDocument } from "pdf-lib";
import { crmRequiredFields } from "./element/constant";
import CrmForm, { paymentBlockOptions } from "./element/crmForm";

/* ================= UTILS ================= */
function base64ToBlob(base64: string, contentType = "", sliceSize = 512) {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Uint8Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(byteNumbers);
  }
  return new Blob(byteArrays, { type: contentType });
}

/* ================= FIELD MAPPER ================= */
const makeField = (original: any, edited: any) => {
  // Deep comparison for arrays/objects
  const isDifferent = JSON.stringify(original) !== JSON.stringify(edited);
  return {
    originalValue: original ?? "",
    editFieldValue: edited !== undefined && isDifferent ? edited : null,
  };
};

export default function EmergingProcess({
  file,
  isOpen,
  onClose,
  item,
  documentId,
  newData = false,
  handleGet,
  name,
  docId,
  activePage,
  asInitiator
}: any): JSX.Element {
  let level = 4;

  const { toastNotify, accountDetails, workflowProcess, workFlowData } =
    useContext(AppContext);
  const viewerWidth = useBreakpointValue({ lg: true });
  const width = file ? "97vw" : "95vw";

  /* ================= FILE STATE ================= */
  const [sample, setSample] = useState<Sample>({
    url: "",
    file: null,
    type: null,
    buffer: null,
    base64: null,
  });
  const [fetchFileLoader, setFetchFileLoader] = useState(false);

  /* ================= MASTER DOCUMENT MODAL LOGIC REMOVED ================= */

  /* ================= FORM DATA ================= */
  const [invoiceDetails, setInvoiceDetails] = useState<any>({});
  const [originalDetails, setOriginalDetails] = useState<any>({});

  /* ================= DECISION ================= */
  const [decisionDrawerOpen, setDecisionDrawerOpen] = useState(false);
  const [decisionType, setDecisionType] = useState<"approved" | "rejected">(
    "approved",
  );
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // Toggle state for file viewer
  const [showFormValidation, setShowFormValidation] = useState(false);
  const [declarationAlertOpen, setDeclarationAlertOpen] = useState(false);
  const [hasShownDeclarationAlert, setHasShownDeclarationAlert] = useState(false);

  /* ================= ADVANCE PAYMENT STATE ================= */
  const [advancePaymentResult, setAdvancePaymentResult] = useState<any>(null);
  const [isAdvancePaymentModalOpen, setAdvancePaymentModalOpen] = useState(false);
  const [hasCheckedAdvancePayment, setHasCheckedAdvancePayment] = useState(false);

  useEffect(() => {
    if (isOpen && invoiceDetails?.Invoice_Date && accountDetails?.level && !hasShownDeclarationAlert) {
      const isLevel2 = accountDetails.level === "level-2";

      const invDate = new Date(invoiceDetails.Invoice_Date);
      const currDate = new Date();
      invDate.setHours(0,0,0,0);
      currDate.setHours(0,0,0,0);

      const diffTime = currDate.getTime() - invDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (isLevel2 && diffDays > 60) {
        setDeclarationAlertOpen(true);
        setHasShownDeclarationAlert(true); // Ensure it only shows once per open
      }
    }
  }, [isOpen, invoiceDetails?.Invoice_Date, accountDetails?.level, hasShownDeclarationAlert]);

  // Reset alert state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setHasShownDeclarationAlert(false);
      setHasCheckedAdvancePayment(false);
    }
  }, [isOpen]);

  /* ================= ADVANCE PAYMENT CHECK ================= */
  useEffect(() => {
    const fetchAdvancePayment = async () => {
      if (
        isOpen &&
        accountDetails?.level === "level-2" &&
        !hasCheckedAdvancePayment &&
        invoiceDetails?.vendorCode &&
        invoiceDetails?.companyCode
      ) {
        setHasCheckedAdvancePayment(true);
        try {
          const res: any = await appRequest("workflow", "checkAdvancePayment", {
            vendorCode: invoiceDetails.vendorCode?.value || invoiceDetails.vendorCode,
            companyCode: invoiceDetails.companyCode?.value || invoiceDetails.companyCode,
          });

          if (res?.status === "success" && res?.data) {
            setAdvancePaymentResult(res.data);
            setAdvancePaymentModalOpen(true);
          }
        } catch (error) {
          console.error("Failed to check advance payment:", error);
        }
      }
    };

    fetchAdvancePayment();
  }, [isOpen, accountDetails?.level, hasCheckedAdvancePayment, invoiceDetails?.vendorCode, invoiceDetails?.companyCode]);



  /* ================= FILE HANDLERS ================= */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const fileURL = URL.createObjectURL(selectedFile);
    setSample({
      url: fileURL,
      file: selectedFile,
      type: selectedFile.type,
      buffer: null,
      base64: null,
    });
  };

  const removeFile = () => {
    if (sample.url && sample.url.startsWith("blob:")) {
      URL.revokeObjectURL(sample.url);
    }
    setSample({
      url: "",
      file: null,
      type: null,
      buffer: null,
      base64: null,
    });
  };

  /* ================= BUILD WORKFLOW PAYLOAD ================= */
  const buildFieldsPayload = () => ({
    Company_Code: makeField(originalDetails.companyCode, invoiceDetails.companyCode),
    Buyer_Name: makeField(originalDetails.buyerName, invoiceDetails.buyerName),
    Buyer_GSTIN_OR_Tax_Identification_Number: makeField(originalDetails.buyerGstIn, invoiceDetails.buyerGstIn),
    Vendor_Code: makeField(originalDetails.vendorCode, invoiceDetails.vendorCode),
    Vendor_Name: makeField(originalDetails.vendorName, invoiceDetails.vendorName),
    Vendor_Address: makeField(originalDetails.vendorAddress, invoiceDetails.vendorAddress),
    Vendor_GSTIN_OR_Tax_Identification_Number: makeField(originalDetails.vendorGstIn, invoiceDetails.vendorGstIn),
    Vendor_PAN: makeField(originalDetails.vendorPan, invoiceDetails.vendorPan),
    paymentTerm: makeField(originalDetails.paymentTerm, invoiceDetails.paymentTerm),
    paymentTermDesc: makeField(originalDetails.paymentTermDesc, invoiceDetails.paymentTermDesc),
    Vendor_Bank_Account: makeField(originalDetails.vendorBankAccount, invoiceDetails.vendorBankAccount),
    Vendor_IFSC_Code: makeField(originalDetails.vendorIfscCode, invoiceDetails.vendorIfscCode),
    Invoice_Number: makeField(originalDetails.Invoice_Number, invoiceDetails.Invoice_Number),
    Invoice_Date: makeField(originalDetails.Invoice_Date, invoiceDetails.Invoice_Date),
    Grand_Total: makeField(originalDetails.Grand_Total, invoiceDetails.Grand_Total),
    Invoice_Type: makeField(originalDetails.invoiceType, invoiceDetails.invoiceType),
    Currency: makeField(originalDetails.currency, invoiceDetails.currency),
    paymentBlock: makeField(originalDetails.paymentBlock, invoiceDetails.paymentBlock),
    Header_Text: makeField(originalDetails.headerText, invoiceDetails.headerText),
    vendorText: makeField(originalDetails.vendorText, invoiceDetails.vendorText),
    postingDate: makeField(originalDetails.postingDate, invoiceDetails.postingDate),
    Reference_1: makeField(originalDetails.reference1, invoiceDetails.reference1),
    Reference_2: makeField(originalDetails.reference2, invoiceDetails.reference2),
    Reference_3: makeField(originalDetails.reference3, invoiceDetails.reference3),
    Line_Items: makeField(originalDetails.lineItems, (invoiceDetails.lineItems || []).map((li: any) => ({
      ...li,
      taxCode: li.taxCode, // fallback for legacy if any
    }))),
    isTdsApplicable: makeField(originalDetails.isTdsApplicable, invoiceDetails.isTdsApplicable),
    tdsSectionType: makeField(originalDetails.tdsSectionType, invoiceDetails.tdsSectionType),
    tdsTaxCode: makeField(originalDetails.tdsTaxCode, invoiceDetails.tdsTaxCode),
    tdsBasicAmount: makeField(originalDetails.tdsBasicAmount, invoiceDetails.tdsBasicAmount),
    tdsTaxCode2: makeField(originalDetails.tdsTaxCode2, invoiceDetails.tdsTaxCode2),
    tdsBasicAmount2: makeField(originalDetails.tdsBasicAmount2, invoiceDetails.tdsBasicAmount2),
    glProfitCenter: makeField(originalDetails.glProfitCenter, invoiceDetails.glProfitCenter),
    glCostCenter: makeField(originalDetails.glCostCenter, invoiceDetails.glCostCenter),
    glBusinessArea: makeField(originalDetails.glBusinessArea, invoiceDetails.glBusinessArea),
    glPlantCode: makeField(originalDetails.glPlantCode, invoiceDetails.glPlantCode),
    glBusinessPlace: makeField(originalDetails.glBusinessPlace, invoiceDetails.glBusinessPlace),
    glInternalOrder: makeField(originalDetails.glInternalOrder, invoiceDetails.glInternalOrder),
    glAccount: makeField(originalDetails.glAccount, invoiceDetails.glAccount),
    glDescription: makeField(originalDetails.glDescription, invoiceDetails.glDescription),
    glAmount: makeField(originalDetails.glAmount, invoiceDetails.glAmount),
    glLineNarration: makeField(originalDetails.glLineNarration, invoiceDetails.glLineNarration),
    glRemark: makeField(originalDetails.glRemark, invoiceDetails.glRemark),
    glNetAmount: makeField(originalDetails.glNetAmount, invoiceDetails.glNetAmount),
    glHsnCode: makeField(originalDetails.glHsnCode, invoiceDetails.glHsnCode),
    glDrCrIndicator: makeField(originalDetails.glDrCrIndicator, invoiceDetails.glDrCrIndicator),
    glTaxCode: makeField(originalDetails.glTaxCode, invoiceDetails.glTaxCode),
    roundOff: makeField(originalDetails.roundOff, invoiceDetails.roundOff),
  });

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  };

  const mergeAllFilesToBase64 = async (files: (File | string)[]) => {
    const mergedPdf = await PDFDocument.create();

    for (const file of files) {
      let bytes: ArrayBuffer;
      if (typeof file === "string") {
        const binaryString = atob(file);
        const len = binaryString.length;
        const u8 = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          u8[i] = binaryString.charCodeAt(i);
        }
        bytes = u8.buffer;
      } else {
        if (file.type !== "application/pdf") continue;
        bytes = await file.arrayBuffer();
      }

      const pdf = await PDFDocument.load(bytes);
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((p) => mergedPdf.addPage(p));
    }
    const mergedBytes: any = await mergedPdf.save();
    return `${arrayBufferToBase64(mergedBytes)}`;
  };

  /* ================= ITEM VALUES → FORM HELPERS ================= */

  /**
   * Flattens a raw line-items array where each row's fields are wrapped as
   * { originalValue, editFieldValue }. Preserves line_item_id.
   * Handles both plain objects (already flat) and wrapped ones.
   */
  const flattenLineItems = (rawItems: any[]): any[] => {
    if (!Array.isArray(rawItems)) return [];
    return rawItems.map((item: any) => {
      const flat: any = {};
      Object.keys(item).forEach((key) => {
        const cell = item[key];
        if (cell && typeof cell === "object" && ("originalValue" in cell || "editFieldValue" in cell)) {
          flat[key] =
            cell.editFieldValue !== null && cell.editFieldValue !== undefined
              ? cell.editFieldValue
              : (cell.originalValue ?? "");
        } else {
          // already a plain value
          flat[key] = cell;
        }
      });
      return flat;
    });
  };

  const getLatestWorkflowValues = (valuesArray: any[] = []) => {
    if (!Array.isArray(valuesArray) || valuesArray.length === 0) return {};

    const latest = valuesArray[valuesArray.length - 1];
    const valuesObj = latest?.values || {};
    const fields = valuesObj.fields || {};
    const result: any = {};

    Object.keys(fields).forEach((key) => {
      const { originalValue, editFieldValue } = fields[key] || {};
      result[key] =
        editFieldValue !== null && editFieldValue !== undefined
          ? editFieldValue
          : (originalValue ?? "");
    });

    // Support both flat lineItems and nested tables.Invoice_line_Items
    const rawLineItems =
      valuesObj.lineItems ||
      valuesObj.tables?.Invoice_line_Items ||
      [];
    result.Line_Items = flattenLineItems(rawLineItems);

    return result;
  };

  /**
   * Converts DD.MM.YYYY or DD/MM/YYYY → YYYY-MM-DD for HTML date inputs.
   * Returns the original string if already ISO or unrecognised.
   */
  const normalizeDateToISO = (raw: string): string => {
    if (!raw) return "";
    // Match DD.MM.YYYY or DD/MM/YYYY
    const match = raw.match(/^(\d{2})[./](\d{2})[./](\d{4})$/);
    if (match) return `${match[3]}-${match[2]}-${match[1]}`;
    return raw; // already YYYY-MM-DD or other
  };

  /**
   * Wraps a plain currency string (e.g. "USD") into { value, label }
   * so the select input can display it. Passes through if already an object.
   */
  const normalizeCurrency = (raw: any): any => {
    if (!raw) return "";
    if (typeof raw === "object") return raw;
    return { value: raw, label: raw };
  };

  const normalizePaymentBlock = (raw: any): any => {
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    const found = paymentBlockOptions.find((p) => p.value === raw || p.label === raw);
    return found || { value: raw, label: raw };
  };

  const mapWorkflowValuesToForm = (v: any = {}) => ({
    companyCode: v.Company_Code || null,
    buyerName: v.Buyer_Name || "",
    buyerGstIn: v.Buyer_GSTIN_OR_Tax_Identification_Number || "",
    vendorCode: v.Vendor_Code || "",
    vendorName: v.Vendor_Name || "",
    vendorAddress: v.Vendor_Address || "",
    vendorGstIn: v.Vendor_GSTIN_OR_Tax_Identification_Number || "",
    vendorPan: v.Vendor_PAN || "",
    paymentTerm: v.paymentTerm || "",
    paymentTermDesc: v.paymentTermDesc || "",
    vendorBankAccount: v.Vendor_Bank_Account || "",
    vendorIfscCode: v.Vendor_IFSC_Code || "",
    Invoice_Number: v.Invoice_Number || "",
    Invoice_Date: normalizeDateToISO(v.Invoice_Date || ""),
    Grand_Total: v.Grand_Total || v.Invoice_Amount || "",
    invoiceType: v.Invoice_Type || null,
    vendorText: v.vendorText || "",
    currency: normalizeCurrency(v.Currency),
    paymentBlock: normalizePaymentBlock(v.paymentBlock || v.Payment_Block),
    headerText: v.Header_Text || "",
    reference1: v.Reference_1 || "",
    reference2: v.Reference_2 || "",
    reference3: v.Reference_3 || "",
    postingDate: v.postingDate || v.Posting_Date || "",
    isTdsApplicable: v.isTdsApplicable ?? false,
    tdsSectionType: v.tdsSectionType || "tds",
    tdsTaxCode: v.tdsTaxCode || null,
    tdsBasicAmount: v.tdsBasicAmount || "",
    tdsTaxCode2: v.tdsTaxCode2 || null,
    tdsBasicAmount2: v.tdsBasicAmount2 || "",
    glProfitCenter: v.glProfitCenter || "",
    glCostCenter: v.glCostCenter || "",
    glBusinessArea: v.glBusinessArea || null,
    glPlantCode: v.glPlantCode || null,
    glBusinessPlace: v.glBusinessPlace || null,
    glInternalOrder: v.glInternalOrder || null,
    glAccount: v.glAccount || "",
    glDescription: v.glDescription || "",
    glAmount: v.glAmount || "",
    glLineNarration: v.glLineNarration || "",
    glRemark: v.glRemark || "",
    glNetAmount: v.glNetAmount || "",
    glHsnCode: v.glHsnCode || "",
    glDrCrIndicator: v.glDrCrIndicator || null,
    glTaxCode: v.glTaxCode || null,
    roundOff: v.roundOff || "0.00",
    lineItems: (v.Line_Items || []).map((item: any) => ({
      ...item,
      glAmount: item.glAmount !== undefined ? item.glAmount : (item.Line_Amount || ""),
      lineNarration: item.lineNarration !== undefined ? item.lineNarration : (item.Item_Description || ""),
      taxCode: item.taxCode || null,
    }))
  });

  /* ================= PREFILL FORM FROM ITEM VALUES ================= */
  useEffect(() => {
    if (item?.values?.length) {
      const latestValues = getLatestWorkflowValues(item.values);
      const mappedForm = mapWorkflowValuesToForm(latestValues);

      setInvoiceDetails(mappedForm);
      setOriginalDetails(mappedForm);
    }
  }, [item]);

  /* ================= SUBMIT DECISION ================= */
  const submitDecision = async (decision: "yes" | "no") => {
    try {
      setLoading(true);

      const globalMandatoryFields = [
        { key: "companyCode", label: "Company Code" },
        { key: "vendorCode", label: "Vendor Code" },
        { key: "vendorName", label: "Vendor Name" },
        { key: "vendorAddress", label: "Vendor Address" },
        { key: "Invoice_Number", label: "Invoice Number" },
        { key: "Invoice_Date", label: "Invoice Date" },
        { key: "Grand_Total", label: "Grand Total" },
        { key: "invoiceType", label: "Invoice Type" },
        { key: "currency", label: "Currency" },
        { key: "headerText", label: "Header Text" },
      ];

      if (decisionType === "approved") {
        const parseNum = (val: any) => {
          if (!val) return 0;
          const num = parseFloat(String(val).replace(/,/g, ""));
          return isNaN(num) ? 0 : num;
        };

        if (String(invoiceDetails?.Invoice_Number || "").length > 16 && decisionType === "approved") {
          toastNotify({
            title: "Invoice Number cannot be greater than 16 characters",
            status: "error",
          });
          return;
        }


        const totalLineItemsAmount = (invoiceDetails.lineItems || []).reduce((acc: number, item: any) => {
          const net = parseFloat(item.netAmount);
          const tax = parseFloat(item.taxAmount);
          return acc + net + tax;
        }, 0);

        const grandTotal = parseFloat(invoiceDetails.Grand_Total);
        const roundOffVal = grandTotal - totalLineItemsAmount;
        const isValid = roundOffVal >= -1 && roundOffVal <= 1;

        const alertMessage = `Round Off Calculation:\n----------------------\nSum of (Net + Tax) Amounts: ${totalLineItemsAmount.toFixed(2)}\nGrand Total: ${grandTotal.toFixed(2)}\nDifference: ${roundOffVal.toFixed(2)}`;

        if (!isValid) {
          window.alert(`${alertMessage}\n\nERROR: Round Off must be between -1 and 1. Submission stopped.`);
          setShowFormValidation(true);
          setLoading(false);
          return;
        } else {
          window.alert(`${alertMessage}\n\nSUCCESS: Round Off is valid. Proceeding with submission...`);
        }

        const missingGlobalFields = globalMandatoryFields.filter((field) => {
          const value = invoiceDetails?.[field.key];
          const actualValue = (typeof value === 'object' && value !== null && 'value' in value) ? value.value : value;
          return !String(actualValue || "").trim();
        });

        const missingLineFields = (invoiceDetails?.lineItems || []).some((li: any) => {
          return !String(li.profitCenter || "").trim() ||
            !String(li.costCenter || "").trim() ||
            !String(li.glAccount || "").trim() ||
            !String(li.glAmount || "").trim() ||
            !String(li.lineNarration || "").trim();
        });

        const missingTdsGlFields = invoiceDetails?.isTdsApplicable && invoiceDetails?.tdsSectionType === "gl" && (
          !String(invoiceDetails.glProfitCenter || "").trim() ||
          !String(invoiceDetails.glPlantCode || "").trim() ||
          !String(invoiceDetails.glBusinessPlace || "").trim() ||
          !String(invoiceDetails.glBusinessArea || "").trim() ||
          !String(invoiceDetails.glAccount || "").trim() ||
          !String(invoiceDetails.glDescription || "").trim() ||
          !String(invoiceDetails.glAmount || "").trim()
        );

        const missingTdsFields = invoiceDetails?.isTdsApplicable && invoiceDetails?.tdsSectionType === "tds" && (
          !invoiceDetails.tdsTaxCode || !String(invoiceDetails.tdsBasicAmount || "").trim()
        );

        if (missingGlobalFields.length > 0 || missingLineFields || missingTdsGlFields || missingTdsFields) {
          setShowFormValidation(true);
          const missingLabels = missingGlobalFields.map(f => f.label);
          if (missingLineFields) missingLabels.push("Line Item fields (Profit/Cost Center, GL, Amount, Narration)");
          if (missingTdsGlFields) missingLabels.push("TDS GL fields (Profit Center, Plant, Business Place, Business Area, GL Account, Description, Amount)");
          if (missingTdsFields) missingLabels.push("TDS Tax Code and Basic Amount");

          toastNotify({
            title: "Validation Error",
            message: `Please fill the following mandatory fields: ${missingLabels.join(", ")}`,
            type: "error",
          });
          setLoading(false);
          return;
        }
      }

      if (asInitiator) {
        if (accountDetails?.level === "level-1" && !sample.file && !sample.base64) {
          toastNotify({
            title: "Validation Error",
            message: "Please upload at least one document.",
            type: "error",
          });
          setLoading(false);
          return;
        }

        let mergedFiles = null;
        let uploadedFileId = null;

        if (sample.base64 || sample.file) {
          const mergeList: (File | string)[] = [];
          if (sample.base64) mergeList.push(sample.base64);
          if (sample.file) mergeList.push(sample.file);

          mergedFiles = await mergeAllFilesToBase64(mergeList);
          if (mergedFiles) {
            const CHUNK_SIZE = 1.5 * 1024 * 1024;
            const totalChunks = Math.ceil(mergedFiles.length / CHUNK_SIZE);
            const uploadId = `upload_${accountDetails?._id || "anon"}_${Date.now()}_${Math.random().toString(36).substring(7)}`;

            for (let i = 0; i < totalChunks; i++) {
              const start = i * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, mergedFiles.length);
              const chunk = mergedFiles.slice(start, end);

              const chunkResponse: any = await appRequest("document", "uploadChunk", {
                uploadId,
                chunkIndex: i,
                totalChunks,
                chunkData: chunk,
                filename: "attachments.pdf",
                contentType: "application/pdf"
              });

              if (chunkResponse?.status === "success" && chunkResponse?.fileId) {
                uploadedFileId = chunkResponse.fileId;
              } else if (chunkResponse?.status === "error") {
                setLoading(false);
                toastNotify({
                  title: "Upload Failed",
                  message: chunkResponse.message || "Failed to upload file chunk",
                  type: "error"
                });
                return;
              }
            }
          }
        }

        const fieldsPayload: any = buildFieldsPayload();
        const { Line_Items, ...fieldsWithoutLines } = fieldsPayload;

        const payload = {
          comment,
          activeWorkflow: workFlowData?._id,
          processName: workflowProcess?.processName,
          processType: workflowProcess?.processType,
          approveDoc: true,
          documentId: item?.documentId,
          originalValues: {
            ...invoiceDetails,
            currency: invoiceDetails?.currency?.value || invoiceDetails?.currency,
            companyCode: invoiceDetails?.companyCode?.value || invoiceDetails?.companyCode,
            vendorCode: invoiceDetails?.vendorCode?.value || invoiceDetails?.vendorCode,
            Invoice_Date: invoiceDetails?.Invoice_Date,
            Invoice_Number: invoiceDetails?.Invoice_Number,
            Grand_Total: invoiceDetails?.Grand_Total,
            invoiceType: invoiceDetails?.invoiceType?.value || invoiceDetails?.invoiceType,
            tdsSectionType: invoiceDetails?.tdsSectionType?.value || invoiceDetails?.tdsSectionType,
            buyerName: invoiceDetails?.buyerName,
            buyerGstIn: invoiceDetails?.buyerGstIn,
            vendorGstIn: invoiceDetails?.vendorGstIn,
            vendorPan: invoiceDetails?.vendorPan,
            paymentTerm: invoiceDetails?.paymentTerm,
            paymentTermDesc: invoiceDetails?.paymentTermDesc,
            vendorBankAccount: invoiceDetails?.vendorBankAccount,
            vendorIfscCode: invoiceDetails?.vendorIfscCode,
            reference1: invoiceDetails?.reference1,
            reference2: invoiceDetails?.reference2,
            reference3: invoiceDetails?.reference3,
            isTdsApplicable: invoiceDetails?.isTdsApplicable,
            tdsTaxCode: invoiceDetails?.tdsTaxCode || invoiceDetails?.tdsTaxCode,
            tdsBasicAmount: invoiceDetails?.tdsBasicAmount,
            tdsTaxCode2: invoiceDetails?.tdsTaxCode2 || invoiceDetails?.tdsTaxCode2,
            tdsBasicAmount2: invoiceDetails?.tdsBasicAmount2,
            glProfitCenter: invoiceDetails?.glProfitCenter,
            glCostCenter: invoiceDetails?.glCostCenter,
            glBusinessArea: invoiceDetails?.glBusinessArea,
            glPlantCode: invoiceDetails?.glPlantCode,
            glBusinessPlace: invoiceDetails?.glBusinessPlace,
            glInternalOrder: invoiceDetails?.glInternalOrder,
            glAccount: invoiceDetails?.glAccount,
            glDescription: invoiceDetails?.glDescription,
            glAmount: invoiceDetails?.glAmount,
            glLineNarration: invoiceDetails?.glLineNarration,
            glRemark: invoiceDetails?.glRemark,
            glNetAmount: invoiceDetails?.glNetAmount,
            glHsnCode: invoiceDetails?.glHsnCode,
            glDrCrIndicator: invoiceDetails?.glDrCrIndicator,
            glTaxCode: invoiceDetails?.glTaxCode,
            roundOff: invoiceDetails?.roundOff,
          },
          editedValues: null,
          finalValues: invoiceDetails,
          values: {
            valuesData: {
              fields: fieldsWithoutLines,
              lineItems: invoiceDetails?.lineItems || [],
            },
          },
          fileId: uploadedFileId || undefined,
          tags: { extraction: { type: "Automatic" } },
        };

        const missingFields = globalMandatoryFields.filter((f) => {
          const value = payload.originalValues?.[f.key];
          const actualValue = (typeof value === 'object' && value !== null && 'value' in value) ? value.value : value;
          return !String(actualValue || "").trim();
        });

        const missingLineFields = (invoiceDetails?.lineItems || []).some((li: any) => {
          return !String(li.profitCenter || "").trim() ||
            !String(li.costCenter || "").trim() ||
            !String(li.glAccount || "").trim() ||
            !String(li.glAmount || "").trim() ||
            !String(li.lineNarration || "").trim();
        });

        const missingTdsGlFields = invoiceDetails?.isTdsApplicable && invoiceDetails?.tdsSectionType === "gl" && (
          !String(invoiceDetails.glProfitCenter || "").trim() ||
          !String(invoiceDetails.glPlantCode || "").trim() ||
          !String(invoiceDetails.glBusinessPlace || "").trim() ||
          !String(invoiceDetails.glBusinessArea || "").trim() ||
          !String(invoiceDetails.glAccount || "").trim() ||
          !String(invoiceDetails.glDescription || "").trim() ||
          !String(invoiceDetails.glAmount || "").trim()
        );

        const missingTdsFields = invoiceDetails?.isTdsApplicable && invoiceDetails?.tdsSectionType === "tds" && (
          !invoiceDetails.tdsTaxCode || !String(invoiceDetails.tdsBasicAmount || "").trim()
        );



        if (missingFields.length || missingLineFields || missingTdsGlFields || missingTdsFields) {
          setShowFormValidation(true);
          const missingLabels = missingFields.map(f => f.label);
          if (missingLineFields) missingLabels.push("Line Item fields (Profit/Cost Center, GL, Amount, Narration)");
          if (missingTdsGlFields) missingLabels.push("TDS GL fields (Profit Center, Plant, Business Place, Business Area, GL Account, Description, Amount)");
          if (missingTdsFields) missingLabels.push("TDS Tax Code and Basic Amount");

          toastNotify({
            title: "Missing Required Fields",
            message: `Please fill the following mandatory fields: ${missingLabels.join(", ")}`,
            type: "error",
          });
          setLoading(false);
          return;
        }

        const response: any = await appRequest("document", "createEmergingDoc", payload);
        if (response?.status === "success") {
          toastNotify({ title: "Success", message: "Decision submitted successfully", type: "success" });
          if (onClose) {
            if (handleGet) handleGet();
            onClose();
          }
          setDecisionDrawerOpen(false);
          setComment("");
        } else {
          toastNotify({ title: "Error", message: response?.data || "Failed to Upload the Document", type: "error" });
        }
      }
      else {
        const fieldsPayload: any = buildFieldsPayload();
        const { Line_Items, ...fieldsWithoutLines } = fieldsPayload;


        const updatedStatus = {
          id: item?._id,
          level: accountDetails?.level,
          status: decisionType,
          comment: comment,
          values: {
            fields: { ...fieldsWithoutLines, postingDate: { originalValue: new Date(), editFieldValue: null }, Posting_Date: { originalValue: new Date(), editFieldValue: null } },
            lineItems: invoiceDetails?.lineItems || [],
          },
          activeWorkflow: workFlowData?._id,
        };

        const response: any = await appRequest("document", accountDetails?.level === "admin" ? "updateByAdmin" : "update", updatedStatus);
        if (response?.status !== "success") throw new Error(response?.message || "Failed to update document");

        if (onClose) {
          onClose();
          if (handleGet) handleGet();
        }
        toastNotify({ title: "Success", message: response?.message || "Updated successfully", type: "success" });
        setDecisionDrawerOpen(false);
        setComment("");
      }
    } catch (err: any) {
      toastNotify({ title: "Error", message: err?.message || "Failed to submit decision", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const getFile = async () => {
    try {
      if (file?._id && item) {
        setFetchFileLoader(true);
        const response: any = await appRequest("document", "previewDocument", { fileId: file?._id });
        if (response && response.base64) {
          const contentType = response.contentType || file.metadata?.contentType || "application/pdf";
          const blob = base64ToBlob(response.base64, contentType);
          const fileURL = URL.createObjectURL(blob);
          setSample({ url: fileURL, file: file, type: contentType, buffer: null, base64: response.base64 });
        }
      }
    } catch (error: any) {
      toastNotify({ title: "Error", message: error.message, type: "error" });
    } finally {
      setFetchFileLoader(false);
    }
  };

  useEffect(() => {
    if (item && file?._id) getFile();
  }, [item, file?._id]);

  const getFilesData = async (fileId: any) => {
      try {
        if (fileId && item) {
          setFetchFileLoader(true);
          const response: any = await appRequest("document", "previewDocument", { fileId });
          if (response && response.base64) {
            const contentType = response.contentType || file.metadata?.contentType || "application/pdf";
            const blob = base64ToBlob(response.base64, contentType);
            const fileURL = URL.createObjectURL(blob);
            setSample({ url: fileURL, file: file, type: contentType, buffer: null, base64: response.base64 });
          }
        }
      } catch (error: any) {
        toastNotify({ title: "Error", message: error.message, type: "error" });
      } finally {
        setFetchFileLoader(false);
      }
    };

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
        <DrawerOverlay />
        <DrawerContent maxW={viewerWidth ? width : "100vw"}>
          <DrawerHeader borderBottomWidth="1px" pb={3}>
            <Flex align="center" gap={6}>
              <Flex align="center" gap={3}>
                <Text fontSize="lg" fontWeight="semibold">CRM Documents</Text>
                {item?.approval && item?.approval?.length > 0 && item?.approval?.[0]?.name && (
                  <Text fontSize="md" color="gray.600">— {item.approval[0].name}</Text>
                )}
              </Flex>
              {accountDetails?.level === "level-1" && (
                <Button
                  as="a"
                  href="/CRM_60_Days_Declaration_format.xlsx"
                  download="CRM_60_Days_Declaration_format.xlsx"
                  colorScheme="blue"
                  size="sm"
                  variant="outline"
                >
                  Download Declaration Template
                </Button>
              )}
            </Flex>
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody height="95vh" p={{ base: 2, lg: 4 }}>
            <Grid templateColumns={{ base: "1fr", lg: isExpanded ? "1fr" : "35% 65%" }} gap={isExpanded ? 0 : 4} width="100%" height="100%" minHeight="0">
              <GridItem minW="0" overflow="hidden" display={isExpanded ? "none" : "flex"} flexDirection="column" position="relative">
                {fetchFileLoader ? (
                  <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                    <Spinner size="xl" thickness="4px" speed="0.65s" color="teal.400" />
                  </Box>
                ) : sample.url ? (
                  <Box flex={1} position="relative" overflow="hidden">
                        <FileViewer activePage={activePage} sample={sample} docId={docId} recallApi={(fileId: any) => { handleGet(); getFilesData(fileId) }} />
                    {asInitiator && (
                      <IconButton
                        position="absolute"
                        top={2}
                        left={2}
                        size="xs"
                        aria-label="remove"
                        icon={<CloseIcon />}
                        onClick={removeFile}
                        colorScheme="red"
                        zIndex={10}
                      />
                    )}
                  </Box>
                ) : <Box flex={1} position="relative" overflow="hidden">
                        <FileViewer activePage={activePage} sample={sample} docId={docId} recallApi={(fileId: any) => { handleGet(); getFilesData(fileId) }} />
                      <IconButton
                        position="absolute"
                        top={2}
                        left={2}
                        size="xs"
                        aria-label="remove"
                        icon={<CloseIcon />}
                        onClick={removeFile}
                        colorScheme="red"
                        zIndex={10}
                      />
                  </Box>}
              </GridItem>
              <GridItem minW="0" overflow="hidden" display={"block"} position="relative">
                <Box position="absolute" top={2} right={2} zIndex={100}>
                  <IconButton aria-label={isExpanded ? "Collapse" : "Expand"} icon={isExpanded ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 14h6v6" /><path d="M20 10h-6V4" /><path d="M14 10l7-7" /><path d="M3 21l7-7" /></svg> : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" /></svg>} size="sm" colorScheme="teal" onClick={() => setIsExpanded(!isExpanded)} opacity={0.8} _hover={{ opacity: 1 }} />
                </Box>
                <Box border="1px solid teal" borderRadius={8} p={{ base: 3, lg: 4 }} height="100%" overflowY="auto">
                  <CrmForm triggerValidation={showFormValidation} gstUser={accountDetails?.level === "level-3"} showTds={accountDetails?.level === "admin" || Number(accountDetails?.level?.split("-")?.[1] || 0) > 3} disabledOtherUserField={!item?.documentId ? false : accountDetails?.level?.split("-")[1] > 2} tdsFifthApproval={accountDetails?.level === "admin" || Number(accountDetails?.level?.split("-")?.[1] || 0) > 4} value={invoiceDetails} onChange={(data: any) => { if (!Object.keys(originalDetails).length) setOriginalDetails(data); setInvoiceDetails(data); }} workflowProcess={workflowProcess} />
                </Box>
              </GridItem>
            </Grid>
          </DrawerBody>
          <DrawerFooter display={(name === "Pending Approval") ? undefined : "none"}>
            <Flex gap={3} display={accountDetails?.level === "admin" ? "none" : undefined}>
              <Button colorScheme="green" onClick={() => { setDecisionType("approved"); setDecisionDrawerOpen(true); }}>{asInitiator ? "Initiate Workflow" : "Accepted"}</Button>
              {!asInitiator && <Button display={documentId ? accountDetails?.level === "admin" ? "none" : undefined : 'none'} colorScheme="red" onClick={() => { setDecisionType("rejected"); setDecisionDrawerOpen(true); }}>Rejected</Button>}
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <Drawer isOpen={decisionDrawerOpen} placement="bottom" onClose={() => setDecisionDrawerOpen(false)} size="md">
        <DrawerOverlay backdropFilter="blur(3px)" />
        <DrawerContent borderTopRadius="2xl" p={1}>
          <DrawerHeader borderBottomWidth="1px" pb={3}>
            <Flex align="center" justify="space-between">
              <Text fontSize="lg" fontWeight="semibold">{decisionType === "approved" ? "Approval Confirmation" : "Rejection Confirmation"}</Text>
              <DrawerCloseButton position="static" />
            </Flex>
          </DrawerHeader>
          <DrawerBody pt={4}>
            <FormControl>
              <FormLabel fontWeight="medium">Comment</FormLabel>
              <Textarea placeholder="Please enter your comment..." value={comment} onChange={(e) => setComment(e.target.value)} resize="none" borderRadius="lg" focusBorderColor={decisionType === "approved" ? "green.400" : "red.400"} />
            </FormControl>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <Flex w="100%" justify="flex-end" gap={3}>
              <Button variant="outline" onClick={() => setDecisionDrawerOpen(false)}>Cancel</Button>
              <Button colorScheme={decisionType === "approved" ? "green" : "red"} isLoading={loading} onClick={() => submitDecision("yes")} minW="120px">{decisionType === "approved" ? "Approve" : "Reject"}</Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <Modal isOpen={declarationAlertOpen} onClose={() => setDeclarationAlertOpen(false)} isCentered size="md">
        <ModalOverlay backdropFilter="blur(5px)" bg="blackAlpha.600" />
        <ModalContent borderRadius="xl" overflow="hidden" boxShadow="2xl" border="1px solid" borderColor="red.100">
          <Box bg="red.50" p={6} textAlign="center">
            <WarningTwoIcon w={10} h={10} color="red.500" mb={3} />
            <ModalHeader p={0} color="red.700" fontSize="xl" fontWeight="bold">
              Action Taken
            </ModalHeader>
          </Box>
          <ModalCloseButton color="red.500" _hover={{ bg: "red.100" }} mt={2} />
          <ModalBody px={8} py={6} bg="white">
            <Text fontSize="md" color="gray.700" textAlign="center" lineHeight="tall">
              Invoice date exceeds 60 days. Please ensure the 60-Day Declaration Form is uploaded in the Additional Documents section.
            </Text>
          </ModalBody>
          <ModalFooter bg="gray.50" justifyContent="center" py={5} borderTop="1px solid" borderColor="gray.100">
            <Button
              colorScheme="teal"
              size="md"
              px={10}
              borderRadius="full"
              boxShadow="md"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg', bg: 'teal.500' }}
              transition="all 0.2s"
              onClick={() => setDeclarationAlertOpen(false)}
            >
              I Understand
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isAdvancePaymentModalOpen} onClose={() => setAdvancePaymentModalOpen(false)} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(3px)" bg="blackAlpha.500" />
        <ModalContent borderRadius="xl" overflow="hidden" boxShadow="xl" border="1px solid" borderColor="teal.100">
          <Box bg="teal.50" p={5} textAlign="center">
            <ModalHeader p={0} color="teal.700" fontSize="lg" fontWeight="bold">
              Advance Payment Details
            </ModalHeader>
          </Box>
          <ModalCloseButton color="teal.500" mt={1} />
          <ModalBody px={6} py={5} bg="white" textAlign="center">
            {advancePaymentResult ? (
              <Flex direction="column" gap={3} align="start" textAlign="left" w="100%">
                {Object.entries(advancePaymentResult).map(([key, value]: any) => (
                  <Flex key={key} w="100%" justify="space-between" borderBottom="1px solid" borderColor="gray.100" pb={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" textTransform="capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}:
                    </Text>
                    <Text fontSize="md" fontWeight="bold" color={key === 'status' && String(value).toLowerCase() === 'success' ? 'green.600' : 'teal.700'}>
                      {String(value)}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <Text>No data available</Text>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" justifyContent="center" py={4} borderTop="1px solid" borderColor="gray.100">
            <Button colorScheme="teal" size="sm" px={8} borderRadius="full" onClick={() => setAdvancePaymentModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Master Document Modal Removed */}
    </>
  );
}
